import styled from '@emotion/styled';
import { Paper } from '@material-ui/core';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortal } from '../../hooks/usePortal';

const Wrapper = styled('div')`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: ${(p) => p.theme.spacing()}px;
  z-index: 5;
`;

export const Popover: React.FC = ({ children }) => {
  return (
    <Wrapper>
      <Paper elevation={10}>{children}</Paper>
    </Wrapper>
  );
};

type Pos = {
  top: number;
  left: number;
};

export type PopoverPosition = 'top' | 'right' | 'bottom' | 'left';

const FixedWrapper = styled('div')<Pos>((p) => {
  return {
    position: 'fixed',
    zIndex: 5000,
    top: p.top,
    left: p.left
  };
});

const OFF_SCREEN: Pos = { top: -10000, left: -10000 };

const getPosition: {
  [K in PopoverPosition]: (anchor: DOMRect, el: DOMRect) => Pos;
} = {
  top: (anchor, el) => {
    return {
      top: anchor.top - el.height,
      left:
        anchor.left + Math.round(anchor.width / 2) - Math.round(el.width / 2)
    };
  },
  bottom: (anchor, el) => {
    return {
      top: anchor.bottom,
      left:
        anchor.left + Math.round(anchor.width / 2) - Math.round(el.width / 2)
    };
  },
  right: (anchor, el) => {
    return {
      left: anchor.left + anchor.width,
      top:
        anchor.top + Math.round(anchor.height / 2) - Math.round(el.height / 2)
    };
  },
  left: (anchor, el) => {
    return {
      left: anchor.left - el.width,
      top:
        anchor.top + Math.round(anchor.height / 2) - Math.round(el.height / 2)
    };
  }
};

const PREFERRED_POSITIONS: { [K in PopoverPosition]: PopoverPosition[] } = {
  top: ['top', 'bottom', 'left', 'right'],
  bottom: ['bottom', 'top', 'left', 'right'],
  left: ['left', 'right', 'top', 'bottom'],
  right: ['right', 'left', 'top', 'bottom']
};

const THRESHOLDS = {
  top: 16, // add topnav height
  right: 16,
  bottom: 16,
  left: 16
};

const Container = styled('div')((p) => ({ margin: p.theme.spacing() }));

type Rect = { top: number; right: number; bottom: number; left: number };

const isRectWithinRect = (
  a: Rect,
  b: Rect,
  margins: Rect = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  }
) => {
  return (
    a.top >= b.top + margins.top &&
    a.right <= b.right - margins.right &&
    a.bottom <= b.bottom - margins.bottom &&
    a.left >= b.left + margins.left
  );
};

const PORTAL_CONTAINER_ID = 'dado-popover-portal-container';

export const FixedPopover: React.FC<{
  open: boolean;
  anchor: HTMLElement | null;
  position?: PopoverPosition;
  thresholds?: { top: number; right: number; bottom: number; left: number };
}> = ({
  open,
  anchor,
  children,
  position = 'top',
  thresholds = THRESHOLDS
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Pos>(OFF_SCREEN);
  const portalContainer = usePortal(PORTAL_CONTAINER_ID);

  useLayoutEffect(() => {
    if (!open || !anchor) {
      setPos(OFF_SCREEN);
      return;
    }
    if (!ref.current) {
      return;
    }
    if (!window.document.scrollingElement) {
      return;
    }

    const el = ref.current;
    const anchorRect = anchor.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const { clientHeight, clientWidth } = window.document.scrollingElement;

    const possiblePositions = PREFERRED_POSITIONS[position];
    // cycle through these positions, check if they are in the viewport, if not, take the one requested
    const bestPos =
      possiblePositions.find((p) => {
        const { width, height } = elRect;
        const { top, left } = getPosition[p](anchorRect, elRect);
        const bottom = top + height;
        const right = left + width;

        return isRectWithinRect(
          {
            top,
            right,
            bottom,
            left
          },
          {
            top: 0,
            right: clientWidth,
            bottom: clientHeight,
            left: 0
          },
          thresholds
        );
      }) || position;

    // as it's plain math, it doesn't matter that we call this twice
    setPos(getPosition[bestPos](anchorRect, elRect));
  }, [open, anchor, position, thresholds]);

  if (!open || !anchor) {
    return null;
  }

  return createPortal(
    <FixedWrapper ref={ref} {...pos}>
      <Container>
        <Paper elevation={10}>{children}</Paper>
      </Container>
    </FixedWrapper>,
    portalContainer
  );
};
